import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAsync, useCart, useStore } from '../lib/store'
import { ErrorNote, Fill, Loading, ProductCard, Shell } from '../components/Layout'

export default function Shop() {
  const [params, setParams] = useSearchParams()
  const { add } = useCart()
  const { settings } = useStore()

  const category = params.get('category') ?? 'All'
  const concern = params.get('concern') ?? ''
  const bestsellersOnly = params.get('filter') === 'Bestsellers'
  const q = params.get('q') ?? ''
  const [search, setSearch] = useState(q)

  const { data, loading, error, reload } = useAsync(
    () => api.products({ category, concern: concern || undefined, q: q || undefined }),
    [category, concern, q]
  )

  const products = (data ?? []).filter((p) => (bestsellersOnly ? p.badge === 'Bestseller' : true))
  const categories = ['All', ...(settings?.categories ?? [])]

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (!value || value === 'All') next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  return (
    <Shell>
      <section className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(280px,0.85fr)', background: 'var(--sand)', alignItems: 'stretch' }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 18, padding: 'clamp(28px,4.4vw,68px)' }}>
          <div className="eyebrow-rose" style={{ color: 'var(--muted)' }}>
            All products · {data?.length ?? 0} formulas
          </div>
          <h1 className="display" style={{ fontSize: 'clamp(30px,3.6vw,52px)', maxWidth: '18ch' }}>
            Everything your skin has been asking for.
          </h1>
          <p className="body-copy" style={{ maxWidth: '36ch' }}>
            Cleansers, serums, moisturisers and treatments — each one formulated to do a single job exceptionally well.
          </p>
          <Link to="/quiz" className="btn btn-solid" style={{ alignSelf: 'flex-start', padding: '14px 30px' }}>
            Not sure? Take the quiz
          </Link>
        </div>
        <div style={{ position: 'relative', minHeight: 'clamp(240px,28vw,400px)' }}>
          <Fill src="/assets/products/retinol-alternative-serum.jpeg" alt="The Lumera range" label="Full product lineup" />
        </div>
      </section>

      <section id="concern" className="section-tight" style={{ paddingBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap', borderBottom: '1px solid var(--line)', paddingBottom: 18 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {categories.map((c) => (
              <button key={c} className="chip" aria-pressed={category === c} onClick={() => setParam('category', c)}>
                {c}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              setParam('q', search.trim())
            }}
            style={{ display: 'flex', border: '1px solid var(--line-2)', background: 'var(--bg)' }}
          >
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products"
              aria-label="Search products"
              style={{ border: 'none', background: 'none', padding: '9px 12px', fontFamily: 'var(--sans)', fontSize: 12, outline: 'none', minWidth: 0, width: 160 }}
            />
            <button type="submit" className="btn btn-sm" style={{ border: 'none', borderLeft: '1px solid var(--line-2)' }} aria-label="Search">
              Go
            </button>
          </form>
        </div>

        {(concern || q || bestsellersOnly) && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 16 }}>
            <span className="small">Filtered by:</span>
            {concern && (
              <button className="chip" onClick={() => setParam('concern', '')}>
                {concern} ✕
              </button>
            )}
            {bestsellersOnly && (
              <button className="chip" onClick={() => setParam('filter', '')}>
                Bestsellers ✕
              </button>
            )}
            {q && (
              <button
                className="chip"
                onClick={() => {
                  setSearch('')
                  setParam('q', '')
                }}
              >
                “{q}” ✕
              </button>
            )}
          </div>
        )}

        <div style={{ marginTop: 14, fontSize: 11, color: 'var(--muted-2)', letterSpacing: '.06em' }}>
          {products.length} {products.length === 1 ? 'product' : 'products'}
        </div>
      </section>

      <section id="bestsellers" className="section" style={{ paddingTop: 'clamp(20px,2.4vw,32px)' }}>
        {loading ? (
          <Loading label="Loading products" />
        ) : error ? (
          <ErrorNote message={error} onRetry={reload} />
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
            <p style={{ margin: '0 0 16px', fontSize: 13 }}>Nothing matches those filters yet.</p>
            <button className="btn btn-sm" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid-products">
            {products.map((p) => (
              <ProductCard key={p.slug} product={p} onAdd={() => add(p.slug, 1, p.name)} />
            ))}
          </div>
        )}
      </section>

      <section className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(280px,1fr)', background: 'var(--blush)', minHeight: 'clamp(280px,28vw,400px)' }}>
        <div style={{ position: 'relative', minHeight: 'clamp(220px,26vw,400px)' }}>
          <Fill src="/assets/products/sensitive-overnight-cream.jpeg" alt="A Lumera routine set" label="Routine set" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 18, padding: 'clamp(28px,4vw,64px)' }}>
          <div className="eyebrow-rose">Routine sets</div>
          <h2 className="display h-lg" style={{ maxWidth: '17ch' }}>
            Three steps, bundled.
          </h2>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 300, lineHeight: 1.8, color: '#4b3c3c', maxWidth: '36ch' }}>
            Cleanse, treat and moisturise — take the quiz and we will build the complete Lumera routine around your skin.
          </p>
          <Link to="/quiz" className="btn" style={{ alignSelf: 'flex-start' }}>
            Build my set
          </Link>
        </div>
      </section>
    </Shell>
  )
}
